/**
 * Domain logic for the migration-invite banner (see
 * src/components/features/migration-banner). Kept out of the .vue component so
 * the event / UTM / dismissal contract is unit-testable without mounting Vue.
 *
 * The banner reuses the SAME SSO handoff as the header dropdown's "Try the new
 * design" item (src/utils/designSwitch.js) so the student lands AUTHENTICATED
 * on edu-new.genius.space, and it reports through the site's EXISTING GTM
 * dataLayer (src/utils/analytics.js) — no second analytics tag.
 */

import { switchToNewDesign } from "./designSwitch";
import {
	trackEvent,
	trackEventOncePerSession,
	setAnalyticsUserId,
} from "./analytics";

// localStorage flag: once dismissed, the banner never nags again on this device.
export const MIGRATION_BANNER_DISMISS_KEY = "migration_banner_dismissed";

// sessionStorage guard so the view event fires at most once per browser session.
export const MIGRATION_BANNER_VIEW_SESSION_KEY = "migration_banner_view_fired";

export const MIGRATION_BANNER_VIEW_EVENT = "migration_banner_view";
export const MIGRATION_BANNER_CLICK_EVENT = "migration_banner_click";

// Appended to the edu-new destination for campaign attribution.
export const MIGRATION_BANNER_UTM = {
	utm_source: "edu_old",
	utm_medium: "banner",
	utm_campaign: "migration",
};

/**
 * @returns {boolean} whether the student already dismissed the banner.
 */
export function isBannerDismissed() {
	try {
		return localStorage.getItem(MIGRATION_BANNER_DISMISS_KEY) === "1";
	} catch (e) {
		return false;
	}
}

/**
 * Persists the dismissal so the banner stays hidden across reloads.
 *
 * @returns {boolean} whether the flag was written.
 */
export function dismissBanner() {
	try {
		localStorage.setItem(MIGRATION_BANNER_DISMISS_KEY, "1");

		return true;
	} catch (e) {
		return false;
	}
}

/**
 * Sets the GA4 user_id to the internal student id when the container hasn't
 * already set one. Never pass an email.
 *
 * @param {string|number} userId internal student id
 * @returns {boolean} whether user_id was set on this call.
 */
export function ensureAnalyticsUser(userId) {
	return setAnalyticsUserId(userId);
}

/**
 * Fires the on-screen view event, at most once per session.
 *
 * @returns {boolean} whether the event fired on this call.
 */
export function trackBannerView() {
	return trackEventOncePerSession(
		MIGRATION_BANNER_VIEW_SESSION_KEY,
		MIGRATION_BANNER_VIEW_EVENT
	);
}

/**
 * CTA handler: fires the click event FIRST (so GA4's sendBeacon transport
 * dispatches it before the page unloads), then performs the SSO handoff to the
 * new app with the migration UTM tags on the destination.
 *
 * @param {string|number} [userId] internal student id (never an email).
 */
export function trackBannerClickAndGo(userId) {
	ensureAnalyticsUser(userId);

	trackEvent(MIGRATION_BANNER_CLICK_EVENT);

	switchToNewDesign({ query: MIGRATION_BANNER_UTM });
}
